import { API_KEY_PREFIX, MAX_API_KEYS, MAX_API_KEY_NAME_LENGTH, type ApiKey } from '@lyrmo/shared';
import { useEffect, useId, useState } from 'react';
import type { ApiClient } from '../../api/client';
import { Button } from '../../components/Button';
import { useClipboard } from '../../hooks/useClipboard';
import { useI18n } from '../../i18n/i18n-context';
import { useAccount } from './account-context';
import { AccountFrame } from './AccountFrame';
import './api-keys.css';

/** The account's API keys: making them, and revoking them. */
function ApiKeys({ client }: { readonly client: ApiClient }) {
  const { t, format, describeError } = useI18n();
  const [keys, setKeys] = useState<ApiKey[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // A new key is only ever shown once, right after it's made.
  const [secret, setSecret] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<string | null>(null);
  const clipboard = useClipboard<'secret'>();
  const nameId = useId();
  const full = keys !== null && keys.length >= MAX_API_KEYS;

  useEffect(() => {
    let current = true;
    client.listApiKeys().then(
      (loaded) => {
        if (current) setKeys(loaded);
      },
      (caught: unknown) => {
        if (current) setLoadError(describeError(caught));
      },
    );
    return () => {
      current = false;
    };
  }, [client, describeError]);

  async function run(action: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (caught) {
      setError(describeError(caught));
    } finally {
      setBusy(false);
    }
  }

  const create = () =>
    run(async () => {
      const created = await client.createApiKey(name.trim());
      setKeys((current) => [...(current ?? []), created.key]);
      setSecret(created.secret);
      setName('');
    });

  const revoke = (key: ApiKey) =>
    run(async () => {
      await client.revokeApiKey(key.id);
      setKeys((current) => (current ?? []).filter((other) => other.id !== key.id));
      setRevoking(null);
    });

  return (
    <div className="api-keys">
      <p className="dialog-intro">
        {t.apiKeys.intro}{' '}
        <a className="api-keys-guide" href="/developers.html" target="_blank" rel="noopener">
          {t.apiKeys.guide}
        </a>
      </p>

      {secret && (
        <section className="api-key-secret" aria-label={t.apiKeys.createdNote}>
          <p>{t.apiKeys.createdNote}</p>
          <code className="api-key-value">{secret}</code>
          <div className="form-actions">
            <Button
              onClick={() => {
                void clipboard.copy('secret', secret);
              }}
            >
              {clipboard.status?.outcome === 'copied' ? t.apiKeys.copied : t.apiKeys.copy}
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setSecret(null);
              }}
            >
              {t.common.done}
            </Button>
          </div>
        </section>
      )}

      {loadError ? (
        <p className="dialog-error" role="alert">
          {loadError}
        </p>
      ) : !keys ? (
        <p className="plan-loading" role="status">
          {t.apiKeys.loading}
        </p>
      ) : keys.length === 0 ? (
        <p className="api-keys-empty">{t.apiKeys.empty}</p>
      ) : (
        <ul className="api-key-list" aria-label={t.apiKeys.title}>
          {keys.map((key) => (
            <li key={key.id} className="api-key-row">
              <div className="api-key-about">
                <p className="api-key-name">{key.name}</p>
                <p className="api-key-meta">
                  <code>
                    {API_KEY_PREFIX}…{key.hint}
                  </code>
                  <span>{t.apiKeys.createdOn(format.date(key.createdAt))}</span>
                  <span>
                    {key.lastUsedAt
                      ? t.apiKeys.lastUsed(format.date(key.lastUsedAt))
                      : t.apiKeys.neverUsed}
                  </span>
                </p>
              </div>
              {revoking === key.id ? (
                <div className="api-key-confirm" role="group" aria-label={t.apiKeys.revoke}>
                  <p>{t.apiKeys.confirmRevoke(key.name)}</p>
                  <div className="api-key-confirm-actions">
                    <Button
                      onClick={() => {
                        setRevoking(null);
                      }}
                    >
                      {t.common.cancel}
                    </Button>
                    <Button variant="primary" disabled={busy} onClick={() => void revoke(key)}>
                      {t.apiKeys.revoke}
                    </Button>
                  </div>
                </div>
              ) : (
                <Button
                  aria-label={t.apiKeys.revokeKey(key.name)}
                  disabled={busy}
                  onClick={() => {
                    setRevoking(key.id);
                  }}
                >
                  {t.apiKeys.revoke}
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}

      <form
        className="api-key-form"
        onSubmit={(event) => {
          event.preventDefault();
          void create();
        }}
      >
        <label className="field-label" htmlFor={nameId}>
          {t.apiKeys.name}
        </label>
        <div className="api-key-create">
          <input
            id={nameId}
            className="text-input"
            maxLength={MAX_API_KEY_NAME_LENGTH}
            placeholder={t.apiKeys.namePlaceholder}
            value={name}
            disabled={full}
            onChange={(event) => {
              setName(event.target.value);
            }}
          />
          <Button type="submit" variant="primary" disabled={busy || full || !name.trim()}>
            {busy ? t.apiKeys.creating : t.apiKeys.create}
          </Button>
        </div>
        {full && <p className="field-hint">{t.apiKeys.limit(MAX_API_KEYS)}</p>}
      </form>

      {error && (
        <p className="dialog-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

export function ApiKeysDialog() {
  const { t } = useI18n();
  const { account, client, dialog, closeDialog, openDialog } = useAccount();
  return (
    <AccountFrame open={dialog === 'api-keys'} onClose={closeDialog} title={t.apiKeys.title}>
      {account?.user ? (
        <ApiKeys client={client} />
      ) : (
        <>
          <p className="dialog-intro">{t.apiKeys.pitch}</p>
          <div className="form-actions">
            <Button onClick={closeDialog}>{t.common.notNow}</Button>
            <Button
              variant="primary"
              onClick={() => {
                openDialog('sign-in');
              }}
            >
              {t.common.signIn}
            </Button>
          </div>
        </>
      )}
    </AccountFrame>
  );
}
